/**
 * お金の画面の「判断」だけを集めた純関数（React・i18n・fetch に依存しない）。
 *
 * WHY 分けるか: 「この状態のクリエイターに何を言うか」（受け取りの登録は終わったか・残高は何日もつか・出金額の入力の読み方）は
 * お金の画面で一番間違えてはいけない部分で、画面の描画と切り離して `node --test` で確かめたい（moneyLogic.test.ts）。
 * このファイルは import を持たない = Node の型の除去（--experimental-strip-types）だけでテストが動く（テスト用の依存を足さない）。
 *
 * 文言はここに書かない。返すのは状態の名前（'ready' 等）で、画面が i18n のキーに引き直す（言語を増やしてもここは変わらない）。
 */

// ---------------------------------------------------------------- 受け取りの登録（Stripe Connect を意識させない）

/**
 * 受け取りの登録の状態を、クリエイターに見せる 1 語にまとめる。
 *   not_started   … まだ始めていない
 *   needs_info    … 追加の情報が要る（Stripe が要件を出している・期限切れ・制限中）→ 「続きを入力」
 *   in_review     … 入力は済み・確認待ち（要件なし・まだ有効でない）→ 待つだけ
 *   ready         … 受け取れる（出金できる）
 *   on_hold       … Kumo の運営が止めている（連絡を待つ）
 *   closed        … 口座が閉じた
 * WHY 口座の状態（transfers / payouts / requirements）をそのまま見せないか: MASTER-PLAN「クリエイターは Stripe を意識しない」。
 * 知りたいのは「受け取れるか・自分が何かすべきか」の 2 つだけ
 */
export type PayeeHeadline = 'not_started' | 'needs_info' | 'in_review' | 'ready' | 'on_hold' | 'closed';

export function payeeHeadline(
  payee: { onboardingStatus: string; requirementsDue: unknown[] | null; requirementsDeadline: string | null; onHold: boolean } | null,
  eligibility: { eligible: boolean; reasons: string[] } | null
): PayeeHeadline {
  if (!payee) return 'not_started';
  if (payee.onboardingStatus === 'closed') return 'closed';
  if (payee.onHold) return 'on_hold';
  if (eligibility?.eligible) return 'ready';
  if (payee.onboardingStatus === 'restricted' || payee.requirementsDeadline === 'past_due' || userActionCount(payee.requirementsDue) > 0) return 'needs_info';
  // 要件の一覧がまだ無いのに「今必要」の期限がある = 作ったばかりで何も入力していない口座（偽の Connect の作成直後もこの形）
  const known = Array.isArray(payee.requirementsDue) ? payee.requirementsDue.length : 0;
  if (known === 0 && payee.requirementsDeadline === 'currently_due') return 'needs_info';
  // 本人の入力待ちが無い = 確認待ち（Stripe 側の審査・税の確認など）。クリエイターが今できることは無い
  return 'in_review';
}

/**
 * 本人が入力すべき要件の数。money/connect.ts の RequirementSummary（awaitingActionFrom = 'user' | 'stripe'）を数える。
 * WHY stripe 待ちを数えないか: 審査中の項目まで「追加の情報が必要です」と出すと、入力を終えた人を不安にさせて何度もフォームを開かせる
 */
export function userActionCount(requirementsDue: unknown[] | null): number {
  if (!Array.isArray(requirementsDue)) return 0;
  return requirementsDue.filter((r) => !(r && typeof r === 'object' && (r as { awaitingActionFrom?: string }).awaitingActionFrom === 'stripe')).length;
}

/**
 * 出金できない理由（money/payees.ts の payoutEligibility の reasons）→ 画面の理由の名前。
 * 同じ意味の理由は 1 つにまとめる（transfers と payouts の両方が未有効でも「確認中」は 1 行）
 */
export type BlockReason = 'setup' | 'review' | 'info' | 'tax' | 'hold' | 'other';

export function blockReasons(reasons: string[]): BlockReason[] {
  const out = new Set<BlockReason>();
  for (const r of reasons) {
    if (r === 'onboarding_not_started') out.add('setup');
    else if (r === 'requirements_past_due' || r === 'onboarding_restricted') out.add('info');
    else if (r === 'onboarding_pending' || r === 'transfers_not_active' || r === 'payouts_not_active') out.add('review');
    else if (r.startsWith('tax_')) out.add('tax');
    else if (r === 'on_hold') out.add('hold');
    else out.add('other');
  }
  // 「まだ始めていない」なら他の理由（未有効など）は言うまでもない
  if (out.has('setup')) return ['setup'];
  return [...out];
}

// ---------------------------------------------------------------- 残高の見通し

/**
 * 前払い残高の状態（色と見出しに使う）。
 *   owed … 請求残がある（Functions など原価の出る部品が止まる・新しいゲームとデプロイが止まる）
 *   low  … 知らせの線（lowBalanceCents）を下回った、または 7 日以内に尽きる見込み
 *   ok / idle（今月の利用が無い）
 * WHY 7 日か: 自動チャージを入れていない人が「気づいて入金する」のに週末を挟んでも間に合う長さ
 */
export type RunwayTone = 'owed' | 'low' | 'ok' | 'idle';
export const RUNWAY_WARN_DAYS = 7;

export function runwayTone(p: { balance: number; owed: number; daysLeft?: number | null; lowBalanceCents: number }): RunwayTone {
  if (p.owed > 0) return 'owed';
  if (p.lowBalanceCents > 0 && p.balance < p.lowBalanceCents) return 'low';
  if (typeof p.daysLeft === 'number' && p.daysLeft < RUNWAY_WARN_DAYS) return 'low';
  if (p.daysLeft === null || p.daysLeft === undefined) return 'idle';
  return 'ok';
}

/**
 * 前払いの画面の色と見出しに使う状態（2026-09-30 console/words-2・walkthrough N1）。
 * サーバーの判定（GET /v1/money/prepaid の balanceStatus = lowBalance.ts balanceCondition = 知らせのメールと同じ判定）を優先し、
 * 画面は runwayTone（7 日の見込み）で「少ない」を足すだけ。
 *   - 線は lowLineCents（いま効いている線）を使う。古い本体（lowLineCents 無し）は設定の線 lowBalanceCents
 *   - balanceStatus が low / out なら、画面の計算が ok でも 'low'（メールが来るのに画面だけ「足りている」を防ぐ）
 *   - 請求残（owed）は最優先（runwayTone と同じ）
 * WHY balanceStatus だけにしないか: 7 日以内に尽きる見込みの「少ない」はサーバーの判定に無い画面独自の先回り。古い本体は balanceStatus も無い
 */
export function prepaidTone(p: {
  balance: number;
  owed: number;
  daysLeft?: number | null;
  lowBalanceCents: number;
  lowLineCents?: number;
  balanceStatus?: string;
}): RunwayTone {
  const tone = runwayTone({ ...p, lowBalanceCents: effectiveLowLine(p) });
  if (tone === 'owed') return tone;
  if (p.balanceStatus === 'low' || p.balanceStatus === 'out') return 'low';
  return tone;
}

/** いま効いている「少ない」の線（セント）。lowLineCents が数として来ていればそれ、無い古い本体は設定の線 */
function effectiveLowLine(p: { lowBalanceCents: number; lowLineCents?: number }): number {
  return typeof p.lowLineCents === 'number' && Number.isFinite(p.lowLineCents) && p.lowLineCents >= 0 ? p.lowLineCents : p.lowBalanceCents;
}

/**
 * 残高の欄の下の「○○を下回ったらお知らせ」に何を出すか（2026-09-30 console/words-2・walkthrough N1・Money 2026-09-30 の文）。
 *   off     … 設定がオフ（0）→「お知らせはオフ」
 *   setting … 効いている線 = 設定の線（古い本体もここ）→ 今までどおり「{line} を下回ったらお知らせ」
 *   invite  … 招待クレジットが残りの半分以上の間、サーバーが線を min(設定, 招待額の 20%) に下げている（Money bf7bd188）
 *             →「招待クレジットを使っている間は、{line} を下回ったらお知らせします（設定: {setting}）」
 * WHY 設定より低いときだけ invite か: サーバーは min を取るので、効いている線が設定より高くなることは無い。
 *   万一高い値が来ても「招待クレジットの間は」の説明は当たらないので、設定の文に倒す（嘘の理由を言わない）
 * WHY 設定がオフなら off か: オフの人には線そのものが無い（サーバーの min(0, …) = 0）
 */
export type BalanceLineHint =
  | { kind: 'off' }
  | { kind: 'setting'; line: number }
  | { kind: 'invite'; line: number; setting: number };

export function balanceLineHint(p: { lowBalanceCents: number; lowLineCents?: number }): BalanceLineHint {
  if (!(p.lowBalanceCents > 0)) return { kind: 'off' };
  const line = effectiveLowLine(p);
  if (line < p.lowBalanceCents) return { kind: 'invite', line, setting: p.lowBalanceCents };
  return { kind: 'setting', line: p.lowBalanceCents };
}

// ---------------------------------------------------------------- 金額の入力

/** 入金 1 回の上下限（最小通貨単位）。サーバーの GET /v1/money/prepaid の topUpLimits（money/prepaid.ts の TOPUP_LIMITS） */
export interface TopUpLimits {
  min: number;
  max: number;
}

/**
 * 入金額が送ってよい範囲か（2026-10-04 console/limits-ui・洗い出し L062）。
 * WHY サーバーの値を使うか: 以前は画面に $5〜$10,000 を写していた。Money が下限を $0.50 に下げても画面が $5 で止め続けないよう、
 *   数字はサーバーから読む。limits が無い（古いサーバー）ときは正の金額なら通し、判定はサーバー（400）に任せる
 */
export function topUpInRange(amount: number | null, limits: TopUpLimits | undefined): boolean {
  if (amount === null || amount <= 0) return false;
  return !limits || (amount >= limits.min && amount <= limits.max);
}

/**
 * 画面の金額の入力（"12.34" / "12" / "$1,234.5"）→ 最小通貨単位の整数。読めない・負・桁が多すぎるなら null。
 * WHY 浮動小数で掛けないか: 4.35 * 100 = 434.99999… を切り捨てると 1 セント少ない出金になる。文字列のまま整数と小数に分ける
 */
export function parseAmountInput(input: string, digits = 2): number | null {
  const s = input.trim().replace(/[\s,$¥￥€£]/g, '');
  if (!s) return null;
  const m = /^(\d{1,9})(?:\.(\d*))?$/.exec(s);
  if (!m) return null;
  const frac = m[2] ?? '';
  if (frac.length > digits) return null;
  return Number(m[1]) * 10 ** digits + (frac ? Number(frac.padEnd(digits, '0')) : 0);
}

/** 最小通貨単位 → 入力欄に入れる文字列（1234 → "12.34"。「全額」ボタン用） */
export function amountToInput(minor: number, digits = 2): string {
  if (digits === 0) return String(minor);
  const s = String(Math.max(0, Math.trunc(minor))).padStart(digits + 1, '0');
  return `${s.slice(0, -digits)}.${s.slice(-digits)}`;
}

/** マイクロ USD → セント（表示用の小数のまま。請求は切り上げだが、内訳の表示は四捨五入しない = 合計と見比べられる） */
export const microsToCents = (micros: number) => micros / 10_000;

// ---------------------------------------------------------------- 出金・パスキー

/** 登録から 72 時間は live の出金を確認できない（money/passkeys.ts。乗っ取り直後の出金を防ぐ） */
export const PASSKEY_LIVE_COOLDOWN_MS = 72 * 3_600_000;

/**
 * そのパスキーで live の出金を確認できるようになる時刻。
 * サーバーは 2026-09-27（payout-security・0161）から `cooldownUntil`（developer_passkeys.cooldown_until）を返す。**それを正とする**:
 * 冷却の長さはサーバーが決める（将来、パスワード再設定の直後に足したパスキーは冷却をやり直す = SEC-ACCESS-05 の後回し分）ので、
 * 画面が「登録 + 72h」を自分で計算すると食い違う。古いサーバー（cooldownUntil が無い）のときだけ登録 + 72h で補う
 */
export function passkeyLiveReadyAt(p: { createdAt: string; cooldownUntil?: string | null }): Date {
  if (p.cooldownUntil) return new Date(p.cooldownUntil);
  return new Date(new Date(p.createdAt).getTime() + PASSKEY_LIVE_COOLDOWN_MS);
}

/**
 * 登録済みのパスキー全体で「live の出金をいつから確認できるか」。
 *   none  … パスキーが無い（追加を促す）
 *   ready … どれか 1 つは冷却が明けている（今すぐ確認できる）
 *   later … 全部が冷却中。at = いちばん早く明ける時刻
 * WHY 一番早いものか: サーバーは冷却の明けたパスキーならどれでも受ける（verifyWithdrawalPasskey）。
 */
export type LiveConfirmReadiness = { state: 'none' } | { state: 'ready' } | { state: 'later'; at: Date };

export function liveConfirmReadiness(passkeys: Array<{ createdAt: string; cooldownUntil?: string | null }>, now: Date): LiveConfirmReadiness {
  if (passkeys.length === 0) return { state: 'none' };
  const times = passkeys.map((p) => passkeyLiveReadyAt(p).getTime());
  if (times.some((t) => t <= now.getTime())) return { state: 'ready' };
  return { state: 'later', at: new Date(Math.min(...times)) };
}

/** 出金の確認トークンの形（サーバーの routes.ts の tokenSchema と同じ） */
const CONFIRM_TOKEN = /^kwc_[A-Za-z0-9_-]{43}$/;
export const isConfirmToken = (s: string) => CONFIRM_TOKEN.test(s);

/**
 * サーバーが返す確認 URL から確認トークンだけを取り出す。2 つの形を読む:
 *   - 旧: `<KUMO_DASHBOARD_URL>/confirm/withdrawal/kwc_…`（2026-09-27 時点のサーバーはまだこの形で返す・routes.ts）
 *   - 新: `<KUMO_DASHBOARD_URL>/confirm/withdrawal#kwc_…`（フラグメント = ブラウザがサーバーに送らない = 静的配信のログに残らない）
 * サーバーが新しい形に切り替えても、この画面は変更なしで動く
 */
export function confirmTokenFromUrl(url: string): string | null {
  const m = /\/confirm\/withdrawal(?:\/|\/?#)(kwc_[A-Za-z0-9_-]{43})(?:[?#/&]|$)/.exec(url);
  return m ? m[1]! : null;
}

/** location.hash（`#kwc_…` / `#token=kwc_…`）→ トークン。形が違えば null（勝手な文字列を API に送らない） */
export function confirmTokenFromHash(hash: string): string | null {
  const h = hash.replace(/^#/, '');
  const v = h.startsWith('token=') ? decodeURIComponent(h.slice(6)) : h;
  return isConfirmToken(v) ? v : null;
}

/**
 * 確認ページの画面内のパス。トークンはフラグメントに置く（SEC-ACCESS-23: パスに載せるとダッシュボードの静的配信・前段のプロキシの
 * アクセスログとブラウザの履歴の URL に残る。フラグメントは HTTP で送られない）
 */
export const confirmPagePath = (token: string) => `/confirm/withdrawal#${token}`;

/**
 * パスキーの削除の確認方法の既定。パスキーが使えるブラウザなら既存のパスキー（持っている証明・パスワードを打たずに済む）、
 * 使えないならパスワード。どちらでもサーバーは受ける（money/passkeys.ts の revokePasskey）ので、画面で切り替えられるようにする
 */
export function defaultRemovalMethod(passkeysSupported: boolean): 'passkey' | 'password' {
  return passkeysSupported ? 'passkey' : 'password';
}

/** 払い出しの状態 → 色（pill の種類） */
export function payoutTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (status === 'paid') return 'ok';
  if (status === 'failed' || status === 'reversed') return 'danger';
  if (status === 'cancelled') return 'neutral';
  return 'warn';
}

// ---------------------------------------------------------------- WebAuthn の JSON（base64url）

/** ArrayBuffer → base64url（パディングなし。サーバーの lib/webauthn.ts と同じ形） */
export function toBase64Url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------- 期間

/** 'YYYY-MM' の直近 n か月（新しい順・UTC）。利用料の月の選択肢 */
export function recentPeriods(now: Date, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(d.toISOString().slice(0, 7));
  }
  return out;
}

/** その月の日付（'YYYY-MM-DD'）を 1 日から、今月なら今日まで並べる（使わなかった日も棒の位置を空けて描く） */
export function daysOfPeriod(period: string, now: Date): string[] {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const isCurrent = now.toISOString().slice(0, 7) === period;
  const end = isCurrent ? now.getUTCDate() : last;
  const out: string[] = [];
  for (let d = 1; d <= end; d++) out.push(`${period}-${String(d).padStart(2, '0')}`);
  return out;
}

/** 'YYYY-MM-DD'（UTC）。期間の指定はサーバーが UTC の日付で受ける */
export const ymd = (d: Date) => d.toISOString().slice(0, 10);

/**
 * 直近 n 日の期間。サーバーの API ごとに to の意味が違うので 2 つの形で返す:
 *   toExclusive … 3D セキュアの集計（from 含む〜to 含まない = 明日の日付）
 *   toInclusive … 売上レポート（両端含む = 今日の日付）
 * WHY まとめるか: 同じ「直近 30 日」を選んでも画面ごとに 1 日ずれる、を防ぐ（今日の分が入るのが正しい）
 */
export function recentRange(now: Date, days: number): { from: string; toInclusive: string; toExclusive: string } {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const from = new Date(today.getTime() - (days - 1) * 86_400_000);
  return { from: ymd(from), toInclusive: ymd(today), toExclusive: ymd(new Date(today.getTime() + 86_400_000)) };
}

// ---------------------------------------------------------------- 3D セキュアの集計（GET /v1/money/three-d-secure）

/** サーバーの threeDSecure.ts の Bucket と同じ形（率は 1 万分率・件数 0 なら null） */
export interface TdsBucket {
  payments: number;
  disputes: number;
  fraudDisputes: number;
  disputeRateBps: number | null;
  fraudDisputeRateBps: number | null;
}

export interface TdsStats extends TdsBucket {
  authenticated: TdsBucket;
  notAuthenticated: TdsBucket & { byResult: Array<TdsBucket & { result: string }> };
  unknown: TdsBucket;
  liabilityShift?: Record<string, TdsBucket>;
}

/**
 * 表の行（見せる順）: 3DS あり → 3DS なし（理由ごと・件数の多い順）→ 不明。
 * WHY 「なし」を理由ごとに割るか: 「試みたが発行会社が未対応」と「Apple Pay（端末で認証済み）」では意味が全然違う。
 * 合計の「なし」だけだと、ウォレットの多いゲームが不必要に危なく見える。
 * WHY 不明を「なし」に混ぜないか: 記録より前の支払い（2026-09-27 以前）が全部「なし」に見えてしまう（サーバーも別に数えている）
 * share = 全支払いに占める割合（0〜1。支払い 0 なら null）
 */
interface TdsRowBase extends TdsBucket {
  share: number | null;
}
export interface TdsSummaryRow extends TdsRowBase {
  kind: 'authenticated' | 'not_authenticated_total' | 'unknown';
}
export interface TdsResultRow extends TdsRowBase {
  kind: 'result';
  result: string;
}
export type TdsRow = TdsSummaryRow | TdsResultRow;

export function tdsRows(s: TdsStats): TdsRow[] {
  const share = (b: TdsBucket) => (s.payments > 0 ? b.payments / s.payments : null);
  const rows: TdsRow[] = [{ kind: 'authenticated', ...pickBucket(s.authenticated), share: share(s.authenticated) }];
  rows.push({ kind: 'not_authenticated_total', ...pickBucket(s.notAuthenticated), share: share(s.notAuthenticated) });
  const results = [...(s.notAuthenticated.byResult ?? [])].sort((a, b) => b.payments - a.payments || a.result.localeCompare(b.result));
  for (const r of results) rows.push({ kind: 'result', result: r.result, ...pickBucket(r), share: share(r) });
  rows.push({ kind: 'unknown', ...pickBucket(s.unknown), share: share(s.unknown) });
  return rows;
}

function pickBucket(b: TdsBucket): TdsBucket {
  return { payments: b.payments, disputes: b.disputes, fraudDisputes: b.fraudDisputes, disputeRateBps: b.disputeRateBps, fraudDisputeRateBps: b.fraudDisputeRateBps };
}

/**
 * 3DS の結果の名前（Stripe の result + Kumo の補い）→ 画面の i18n キーの末尾。知らない値は 'other'（画面は生の値を添えて出す）。
 * Stripe が将来値を足しても画面が壊れない
 */
const TDS_RESULTS = ['attempt_acknowledged', 'exempted', 'not_supported', 'failed', 'processing_error', 'data_share_only', 'none', 'wallet', 'not_card'] as const;
export type TdsResultKey = (typeof TDS_RESULTS)[number] | 'other';
export const tdsResultKey = (r: string): TdsResultKey => ((TDS_RESULTS as readonly string[]).includes(r) ? (r as TdsResultKey) : 'other');

/**
 * 申し立て率の目安の色。0.75%（75 bps）= Kumo の申し立て率のシグナルの閾値（disputes.ts・カードブランドの監視の目安 ~0.9% より手前）。
 * 支払いが 3 件未満なら判断しない（1 件の申し立てで 33% になる = 数字が怖く見えるだけ。サーバーのシグナルも最低 3 件）
 */
export const DISPUTE_WARN_BPS = 75;
export const DISPUTE_MIN_PAYMENTS = 3;
export function disputeTone(b: TdsBucket): 'ok' | 'warn' | 'neutral' {
  if (b.payments < DISPUTE_MIN_PAYMENTS || b.disputeRateBps === null) return 'neutral';
  return b.disputeRateBps >= DISPUTE_WARN_BPS ? 'warn' : 'ok';
}

// ---------------------------------------------------------------- 売上レポート（GET /v1/money/reports/sales）

export interface SalesRowLike {
  period?: string;
  projectId?: string;
  projectName?: string | null;
  source: string;
  currency: string;
  count: number;
  gross: number;
  refunds: number;
  fees: number;
  net: number;
}

export interface SalesGroup {
  key: string;
  label: { kind: 'period' | 'project' | 'source'; value: string; name?: string | null };
  currency: string;
  count: number;
  gross: number;
  refunds: number;
  fees: number;
  net: number;
  /** groupBy が source 以外のとき、その行の内訳（source ごと） */
  sources: string[];
}

/**
 * サーバーの行（常に source × currency で分かれる）を、選んだ切り口（日 / 月 / ゲーム / 収入源）× 通貨の 1 行にまとめる。
 * WHY 画面で束ねるか: 「9 月 3 日にいくら入ったか」を見たい人に、購入と広告の 2 行を並べると足し算を強いる。
 * 収入源の内訳は sources に残す（行の下に小さく出す）。通貨はまとめない（USD と CAD を足すと嘘の数字になる）。
 * 並び: 日・月は新しい順（最近を上に）、ゲーム・収入源は手取りの多い順
 */
export function groupSalesRows(rows: SalesRowLike[], groupBy: 'day' | 'month' | 'project' | 'source'): SalesGroup[] {
  const map = new Map<string, SalesGroup>();
  for (const r of rows) {
    const label: SalesGroup['label'] =
      groupBy === 'project'
        ? { kind: 'project', value: r.projectId ?? '', name: r.projectName ?? null }
        : groupBy === 'source'
          ? { kind: 'source', value: r.source }
          : { kind: 'period', value: r.period ?? '' };
    const key = `${label.value}|${r.currency}`;
    let g = map.get(key);
    if (!g) {
      g = { key, label, currency: r.currency, count: 0, gross: 0, refunds: 0, fees: 0, net: 0, sources: [] };
      map.set(key, g);
    }
    g.count += r.count;
    g.gross += r.gross;
    g.refunds += r.refunds;
    g.fees += r.fees;
    g.net += r.net;
    if (!g.sources.includes(r.source)) g.sources.push(r.source);
  }
  const out = [...map.values()];
  if (groupBy === 'day' || groupBy === 'month') out.sort((a, b) => b.label.value.localeCompare(a.label.value) || a.currency.localeCompare(b.currency));
  else out.sort((a, b) => b.net - a.net || a.key.localeCompare(b.key));
  return out;
}

/** 期間の合計（通貨ごと）。合計の行・タイルに使う */
export function salesTotals(rows: SalesRowLike[]): Array<{ currency: string; count: number; gross: number; refunds: number; fees: number; net: number }> {
  const map = new Map<string, { currency: string; count: number; gross: number; refunds: number; fees: number; net: number }>();
  for (const r of rows) {
    const t = map.get(r.currency) ?? { currency: r.currency, count: 0, gross: 0, refunds: 0, fees: 0, net: 0 };
    t.count += r.count;
    t.gross += r.gross;
    t.refunds += r.refunds;
    t.fees += r.fees;
    t.net += r.net;
    map.set(r.currency, t);
  }
  return [...map.values()].sort((a, b) => b.net - a.net || a.currency.localeCompare(b.currency));
}

// ---------------------------------------------------------------- 振込先（銀行口座）の促し

/**
 * 振込先が銀行口座でない / 無い受取人への促し。
 * WHY: Stripe はデビットカードからマイナス残高を引き落とせない（money/connect.ts の createOnboardingSession の注記）。
 * 払い出し後の返金・チャージバックを口座から回収できないと、Kumo の Stripe 残高から埋められ（180 日）作者の請求残に残る。
 * 登録フォームで「銀行口座だけ」に絞る指定が Stripe に無いので、画面で銀行口座の登録を促す（README「未確定」）。
 *
 * 振込先の種類はサーバーの GET /v1/money/payee が**まだ返さない**（2026-09-27）。返すようになったら `payoutMethod` を読む:
 *   add_bank     … 振込先が無い（登録は済んだのに口座が未登録）
 *   replace_card … 振込先がデビットカードだけ
 *   advise       … 種類が分からない（今のサーバー）→ 押し付けない一般の案内（「銀行口座で登録してください」）
 *   null         … 銀行口座あり、または登録をまだ始めていない（登録の案内が先）
 */
export type PayoutMethodInfo = { hasBankAccount: boolean; hasCard: boolean } | null | undefined;
export type BankPrompt = 'add_bank' | 'replace_card' | 'advise' | null;

export function bankAccountPrompt(payee: { onboardingStatus: string } | null, payoutMethod: PayoutMethodInfo): BankPrompt {
  if (!payee || payee.onboardingStatus === 'closed') return null;
  if (payoutMethod === undefined) return 'advise';
  if (payoutMethod === null) return 'add_bank';
  if (payoutMethod.hasBankAccount) return null;
  return payoutMethod.hasCard ? 'replace_card' : 'add_bank';
}
